import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  FormControl,
  FormLabel,
  Select,
  useColorModeValue,
  Badge,
  Divider,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { useContext } from 'react';
import { AppContext } from './AppContext';

const AnalyzeCase = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('pretty'); // 'pretty' or 'raw'
  const [selectedPayer, setSelectedPayer] = useState('BCBS');

  // ALL HOOKS MUST BE AT TOP LEVEL - MOVED TO FIX ESLINT ERROR
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const codeBlockBg = useColorModeValue('gray.50', 'gray.800');
  const instructionsBg = useColorModeValue('blue.50', 'blue.900');
  const keyBg = useColorModeValue('gray.100', 'gray.600');

  const analyzeCase = async () => {
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await callApi('/process_case_complete', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          carrier_info: {
            payer_type: selectedPayer,
            state_full: "Texas"
          },
          options: {
            include_icd10: true,
            include_medical_necessity: true,
            force_refresh: false
          },
          background: false
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }

      setResults(data);
    } catch (err) {
      console.error('Error analyzing case:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine if a string should be rendered as markdown
  const shouldRenderAsMarkdown = (value) => {
    if (typeof value !== 'string') return false;
    return value.length > 50 || value.includes('\n') || value.includes('**') || value.includes('##');
  };

  // Helper function to format object keys for display
  const formatKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Recursive component to render JSON with smart formatting
  const JsonRenderer = ({ data, depth = 0 }) => {
    if (data === null || data === undefined) {
      return <Text color="gray.500" fontStyle="italic">null</Text>;
    }

    if (typeof data === 'boolean') {
      return <Badge colorScheme={data ? 'green' : 'red'}>{data.toString()}</Badge>;
    }

    if (typeof data === 'number') {
      return <Text fontWeight="semibold" color="blue.600">{data}</Text>;
    }

    if (typeof data === 'string') {
      if (shouldRenderAsMarkdown(data)) {
        return (
          <Box
            p={3}
            bg={codeBlockBg}
            borderRadius="md"
            border="1px solid"
            borderColor={borderColor}
          >
            <ReactMarkdown>{data}</ReactMarkdown>
          </Box>
        );
      }
      return <Text>{data}</Text>;
    }

    if (Array.isArray(data)) {
      if (data.length === 0) {
        return <Text color="gray.500" fontStyle="italic">[]</Text>;
      }
      return (
        <VStack align="stretch" spacing={2} pl={4}>
          {data.map((item, index) => (
            <Box key={index}>
              <Text fontSize="sm" color="gray.500" mb={1}>
                [{index}]
              </Text>
              <JsonRenderer data={item} depth={depth + 1} />
            </Box>
          ))}
        </VStack>
      );
    }

    if (typeof data === 'object') {
      const entries = Object.entries(data);
      if (entries.length === 0) {
        return <Text color="gray.500" fontStyle="italic">{'{}'}</Text>;
      }

      return (
        <VStack align="stretch" spacing={3} pl={depth > 0 ? 4 : 0}>
          {entries.map(([key, value], index) => (
            <Box key={key}>
              <HStack align="flex-start" spacing={3}>
                <Text
                  fontSize="sm"
                  fontWeight="bold"
                  bg={keyBg}
                  px={2}
                  py={1}
                  borderRadius="md"
                  minW="fit-content"
                >
                  {formatKey(key)}
                </Text>
                <Box flex={1}>
                  <JsonRenderer data={value} depth={depth + 1} />
                </Box>
              </HStack>
              {index < entries.length - 1 && <Divider mt={2} />}
            </Box>
          ))}
        </VStack>
      );
    }

    return <Text>{String(data)}</Text>;
  };

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box>
            <Heading as="h1" size="xl" mb={2}>
              Complete Case Workflow Analysis
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Comprehensive case processing: CPT coding, ICD-10 coding, carrier assignment, and medical necessity analysis
            </Text>
          </Box>

          {/* Case Info Card */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">Case Information</Heading>
            </CardHeader>
            <CardBody>
              <Text fontSize="lg">
                <strong>Case ID:</strong> {caseId}
              </Text>
            </CardBody>
          </Card>

          {/* Controls Card */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">Workflow Settings</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Payer Type (for testing)</FormLabel>
                  <Select value={selectedPayer} onChange={(e) => setSelectedPayer(e.target.value)}>
                    <option value="BCBS">BCBS</option>
                    <option value="Aetna">Aetna</option>
                    <option value="Cigna">Cigna</option>
                    <option value="Humana">Humana</option>
                    <option value="UHC">UHC</option>
                    <option value="CMS">CMS/Medicare</option>
                  </Select>
                </FormControl>

                <Button
                  colorScheme="blue"
                  size="lg"
                  onClick={analyzeCase}
                  isLoading={loading}
                  loadingText="Processing Complete Workflow..."
                  isDisabled={loading}
                >
                  Run Complete Workflow Analysis
                </Button>
              </VStack>
            </CardBody>
          </Card>

          {/* Loading State */}
          {loading && (
            <Card bg={cardBg} borderColor={borderColor}>
              <CardBody>
                <VStack spacing={4}>
                  <Spinner size="xl" color="blue.500" />
                  <Text fontSize="lg">Processing complete workflow...</Text>
                  <Text fontSize="sm" color="gray.500">
                    Running CPT coding, ICD-10 coding, carrier assignment, carrier adjustments, and medical necessity analysis.
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    This comprehensive analysis may take 30-60 seconds.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          )}

          {/* Error Display */}
          {error && !loading && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>Workflow Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Box>
            </Alert>
          )}

          {/* Results Display */}
          {results && !loading && !error && (
            <Card bg={cardBg} borderColor={borderColor}>
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="md">Complete Workflow Results</Heading>
                  <HStack spacing={3}>
                    {results.success !== undefined && (
                      <Badge
                        colorScheme={results.success ? 'green' : 'red'}
                        fontSize="sm"
                        px={3}
                        py={1}
                      >
                        {results.success ? 'SUCCESS' : 'FAILED'}
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setViewMode(viewMode === 'pretty' ? 'raw' : 'pretty')}
                    >
                      {viewMode === 'pretty' ? 'Show Raw JSON' : 'Show Pretty View'}
                    </Button>
                  </HStack>
                </HStack>
              </CardHeader>
              <CardBody>
                {viewMode === 'pretty' ? (
                  <JsonRenderer data={results} />
                ) : (
                  <Box
                    as="pre"
                    whiteSpace="pre-wrap"
                    fontFamily="monospace"
                    fontSize="sm"
                    p={4}
                    bg={codeBlockBg}
                    borderRadius="md"
                    overflow="auto"
                    maxHeight="600px"
                    border="1px solid"
                    borderColor={borderColor}
                  >
                    {JSON.stringify(results, null, 2)}
                  </Box>
                )}
              </CardBody>
            </Card>
          )}

          {/* Instructions */}
          {!results && !loading && !error && (
            <Card bg={instructionsBg} borderColor="blue.200">
              <CardBody>
                <VStack spacing={3} align="start">
                  <Heading size="sm">Complete Workflow Analysis:</Heading>
                  <Text fontSize="sm">
                    1. Select a payer type for testing different carrier scenarios
                  </Text>
                  <Text fontSize="sm">
                    2. Click "Run Complete Workflow Analysis" to process the entire case
                  </Text>
                  <Text fontSize="sm">
                    3. The workflow includes: CPT coding → ICD-10 coding → Carrier assignment → Carrier adjustments → Medical necessity analysis
                  </Text>
                  <Text fontSize="sm">
                    4. Results will show comprehensive analysis with all workflow steps and detailed medical necessity assessment
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <strong>Current Case ID:</strong> {caseId}
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default AnalyzeCase;
